import { useState, useEffect, useCallback, useRef } from 'react';
import { IonIcon } from '@ionic/react';
import { calendarNumberOutline, callSharp } from 'ionicons/icons';
import { listCalls, getCallById, publishCall, rescoreCall, setDoNotCount, setDoesCountReason, cancelScheduledCall, patchScheduledCall, checkCollision } from '../../services/callService';
import type { CollisionResult } from '../../services/callService';
import { searchCallVoices } from '../../services/callVoiceService';
import { searchCallPhoneNumbers } from '../../services/callPhoneNumberService';
import type { CallVoice } from '../../types/callVoice';
import type { CallPhoneNumber } from '../../types/callPhoneNumber';
import { EmailCallModal } from './EmailCallModal';
import { ScoreBadge } from './shared';
import { formatDateForApi, formatDateTimeInTimezone } from './utils';
import { useProfileStore } from '../../store/useProfileStore';
import { DateFilterModal } from './DateFilterModal';
import { CallDetailDrawer } from './CallDetailDrawer';
import { AssociatePropertyModal } from './AssociatePropertyModal';
import { AssociateAgentModal } from './AssociateAgentModal';
import { AssociateCallReportModal } from './AssociateCallReportModal';
import { CallDataModal } from './CallDataModal';
import { CreateCallModal } from './CreateCallModal';
import { ViewVoiceModal } from './ViewVoiceModal';
import { ViewPhoneCallsModal } from './ViewPhoneCallsModal';
import { ViewInputVarsModal } from './ViewInputVarsModal';
import { ViewAIAgentModal } from '../ai-agents/ViewAIAgentModal';
import { ViewCallReportModal } from '../call-reports/ViewCallReportModal';
import { searchAIAgents } from '../../services/aiAgentService';
import type { RetellAIAgent } from '../../types/aiAgent';
import type { RetellCallReport } from '../../types/callReport';
import type { MysteryShopCall } from '../../types/mysteryShop';
import { useRefresh } from '../../contexts/RefreshContext';
import { searchClients } from '../../services/clientService';
import { searchProperties, getPropertyById } from '../../services/propertyService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';
import type { CallFilters } from '../../types/callFilter';

interface CallListProps {
  title?: string;
  filter?: Partial<CallFilters>;
}

function getDefaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

export function CallList({ title = 'Calls', filter: parentFilter }: CallListProps) {
  const { activeProfile } = useProfileStore();
  const defaults = getDefaultDates();
  const [data, setData] = useState<MysteryShopCall[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [orphaned, setOrphaned] = useState(false);
  const [demoSegment, setDemoSegment] = useState<'clients' | 'demo'>('clients');
  const [callTypeSegment, setCallTypeSegment] = useState<'called' | 'scheduled'>('called');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [associatePropertyCallId, setAssociatePropertyCallId] = useState<string | null>(null);
  const [associateAgentCall, setAssociateAgentCall] = useState<{ id: string; propertyId: string | null } | null>(null);
  const [associateCallReportCall, setAssociateCallReportCall] = useState<{ id: string; retellAgentId?: string } | null>(null);
  const [showCreateCallModal, setShowCreateCallModal] = useState(false);
  const [emailCallId, setEmailCallId] = useState<string | null>(null);
  const [reasonCallId, setReasonCallId] = useState<string | null>(null);
  const [reasonInput, setReasonInput] = useState('');
  const [cancelCallId, setCancelCallId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [editScheduledCallId, setEditScheduledCallId] = useState<string | null>(null);
  const [viewDataCallId, setViewDataCallId] = useState<string | null>(null);
  const [viewAIAgent, setViewAIAgent] = useState<RetellAIAgent | null>(null);
  const [viewCallReport, setViewCallReport] = useState<RetellCallReport | null>(null);
  const [viewVoiceId, setViewVoiceId] = useState<string | null>(null);
  const [viewPhoneNumber, setViewPhoneNumber] = useState<string | null>(null);
  const [viewInputVars, setViewInputVars] = useState<Record<string, string> | null>(null);
  const [viewErrors, setViewErrors] = useState<string[] | null>(null);

  // Client/Property filter dropdowns
  const [filterClientId, setFilterClientId] = useState('');
  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [filterProperties, setFilterProperties] = useState<Property[]>([]);
  const [fixedPropertyName, setFixedPropertyName] = useState('');

  const hasParentClient = !!parentFilter?.clientId;
  const hasParentProperty = !!parentFilter?.propertyId;
  const showClientDropdown = !hasParentClient && !hasParentProperty;
  const showPropertyDropdown = !(hasParentClient && hasParentProperty);
  const propertyDropdownDisabled = hasParentProperty;
  const effectiveClientId = parentFilter?.clientId || filterClientId;

  // Load clients for dropdown (only when neither parent filter is set)
  useEffect(() => {
    if (parentFilter?.clientId || parentFilter?.propertyId) return;
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, [parentFilter?.clientId, parentFilter?.propertyId]);

  // Load property name for disabled display (propertyId known without clientId)
  useEffect(() => {
    if (!parentFilter?.propertyId || parentFilter?.clientId) return;
    getPropertyById(parentFilter.propertyId).then(p => setFixedPropertyName(p.name));
  }, [parentFilter?.propertyId, parentFilter?.clientId]);

  // Load properties for dropdown
  useEffect(() => {
    if (parentFilter?.propertyId) return;
    const cId = parentFilter?.clientId || filterClientId;
    if (!cId) { setFilterProperties([]); return; }
    searchProperties({ clientId: cId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setFilterProperties);
  }, [parentFilter?.propertyId, parentFilter?.clientId, filterClientId]);

  // Reset property selection when client changes
  useEffect(() => {
    setFilterPropertyId('');
    setPage(1);
  }, [filterClientId]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter: CallFilters = { ...parentFilter };
      if (orphaned) filter.orphaned = true;
      if (filterClientId) filter.clientId = filterClientId;
      if (filterPropertyId) filter.propertyId = filterPropertyId;
      if (!hasParentClient && !hasParentProperty) filter.demo = demoSegment === 'demo';
      filter.callStatus = callTypeSegment === 'called' ? 'placed' : 'scheduled';

      const startApi = formatDateForApi(new Date(startDate + 'T00:00:00'));
      const endApi = formatDateForApi(new Date(endDate + 'T23:59:59'));

      const results = await listCalls(filter, {
        page,
        pageSize,
        orderBy: callTypeSegment === 'scheduled' ? 'created' : 'created',
        order: callTypeSegment === 'scheduled' ? 'asc' : 'desc',
        startDate: startApi,
        endDate: endApi,
        expand: 'retellCallReport',
      });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, orphaned, startDate, endDate, parentFilter, filterClientId, filterPropertyId, demoSegment, callTypeSegment, hasParentClient, hasParentProperty]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/calls', fetch);
    return () => unregisterRefreshHandler('/calls');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  async function refreshRow(callId: string) {
    const updated = await getCallById(callId);
    setData((prev) => prev.map((c) => (c.id === callId ? updated : c)));
    return updated;
  }

  const pollingRef = useRef<ReturnType<typeof setInterval>[]>([]);

  function startPolling(callId: string) {
    let ticks = 0;
    const interval = setInterval(async () => {
      ticks++;
      try {
        const updated = await refreshRow(callId);
        if (updated.callEnded || ticks >= 20) clearInterval(interval);
      } catch {
        clearInterval(interval);
      }
    }, 30_000);
    pollingRef.current.push(interval);
  }

  useEffect(() => {
    return () => pollingRef.current.forEach((id) => clearInterval(id));
  }, []);

  async function handleViewAIAgent(retellAgentId: string) {
    const results = await searchAIAgents({ retellAgentId });
    if (results.length > 0) setViewAIAgent(results[0]);
  }

  const hasNext = data.length === pageSize;
  const hasPrev = page > 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header toolbar */}
      <div className="flex items-center px-4 py-3 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {!hasParentClient && !hasParentProperty && (
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden mx-auto">
            <button
              onClick={() => { setDemoSegment('clients'); setFilterClientId(''); setPage(1); }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                demoSegment === 'clients' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Clients
            </button>
            <button
              onClick={() => { setDemoSegment('demo'); setFilterClientId(''); setPage(1); }}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                demoSegment === 'demo' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Demo Accounts
            </button>
          </div>
        )}
        <div className={`inline-flex rounded-lg border border-gray-300 overflow-hidden${hasParentClient || hasParentProperty ? ' mx-auto' : ''}`}>
          <button type="button" onClick={() => { setCallTypeSegment('called'); setPage(1); }} className={`px-3 py-1.5 text-sm font-medium transition-colors ${callTypeSegment === 'called' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Called</button>
          <button type="button" onClick={() => { setCallTypeSegment('scheduled'); setPage(1); }} className={`px-3 py-1.5 text-sm font-medium transition-colors ${callTypeSegment === 'scheduled' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Scheduled</button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={orphaned}
              onChange={(e) => { setOrphaned(e.target.checked); setPage(1); }}
              className="rounded border-gray-300"
            />
            <span title="A call that came in but couldn't be matched to a property.">Orphaned</span>
          </label>
          {showClientDropdown && (() => {
            const filteredClients = clients.filter(c => demoSegment === 'demo' ? !!c.demo : !c.demo);
            return (
              <select
                value={filterClientId}
                onChange={(e) => { setFilterClientId(e.target.value); setPage(1); }}
                className="text-sm border border-gray-200 rounded px-2 py-1"
              >
                <option value="">All {demoSegment === 'demo' ? 'Demo Accounts' : 'Clients'}</option>
                {filteredClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            );
          })()}
          {showPropertyDropdown && (
            propertyDropdownDisabled ? (
              <select disabled className="text-sm border border-gray-200 rounded px-2 py-1 bg-gray-100 cursor-not-allowed">
                <option>{fixedPropertyName || 'Loading...'}</option>
              </select>
            ) : (
              <select
                value={filterPropertyId}
                onChange={(e) => { setFilterPropertyId(e.target.value); setPage(1); }}
                disabled={!effectiveClientId}
                className={`text-sm border border-gray-200 rounded px-2 py-1 ${!effectiveClientId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">{effectiveClientId ? 'All Properties' : 'Select client first'}</option>
                {filterProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )
          )}
          <button
            onClick={() => setShowFilterModal(true)}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Date Range"
          >
            <IonIcon icon={calendarNumberOutline} style={{ fontSize: '16px' }} />
          </button>
          <button
            onClick={() => setShowCreateCallModal(true)}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Create Call"
          >
            <IonIcon icon={callSharp} style={{ fontSize: '16px' }} />
          </button>
          <button
            onClick={fetch}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"/>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
              <path d="M3 22v-6h6"/>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Row list */}
      <div className="min-h-[300px]">
        {loading ? (
          <div className="px-4 py-12 text-center text-gray-400">Loading...</div>
        ) : data.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-400">No results found</div>
        ) : (
          data.map((call) => (
            <div
              key={call.id}
              className="flex items-start gap-6 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
            >
              {/* Columns 1–4 */}
              <div className="flex items-start gap-6 flex-1">
                {/* 1. Property */}
                <div className="min-w-[140px] flex-1">
                  {call.clientId && (() => {
                    const client = clients.find(c => c.id === call.clientId);
                    return client ? (
                      <>
                        <p className="text-xs text-gray-400">Client</p>
                        <p className="font-semibold text-gray-900">{client.name}</p>
                      </>
                    ) : null;
                  })()}
                  <p className="text-xs text-gray-400">Property</p>
                  {call.propertyId ? (
                    <p className="font-semibold text-gray-900">{call.propertyName}</p>
                  ) : (
                    <button
                      disabled={!call.retellCallReport}
                      onClick={() => setAssociatePropertyCallId(call.id)}
                      className={`text-xs px-2 py-1 rounded font-medium transition-colors mt-0.5 ${
                        call.retellCallReport
                          ? 'bg-[#800000] text-white hover:bg-[#660000]'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Associate Property
                    </button>
                  )}
                  {callTypeSegment === 'scheduled' && call.callDate && (
                    <div className="mt-1">
                      <p className="text-xs text-gray-400">Scheduled For</p>
                      <p className="font-semibold text-blue-600">
                        {new Date(call.callDate).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}, {new Date(call.callDate).toLocaleTimeString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </p>
                      {call.callStatus === 'scheduled' && (
                        <button style={{ width: '100px' }}
                          onClick={() => setEditScheduledCallId(call.id)}
                          className="text-xs px-2 py-1 rounded font-medium transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 mt-1"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* 2. Agent (hidden for scheduled calls) */}
                {callTypeSegment !== 'scheduled' && <div className="w-[150px]">
                  <p className="text-xs text-gray-400">Agent</p>
                  {!call.agentId && (
                    <button
                      disabled={!call.propertyId}
                      onClick={() => setAssociateAgentCall({ id: call.id, propertyId: call.propertyId })}
                      className={`text-xs px-2 py-1 rounded font-medium transition-colors mt-0.5 ${
                        call.propertyId
                          ? 'bg-[#800000] text-white hover:bg-[#660000]'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Associate Agent
                    </button>
                  )}
                  <p className={`font-semibold ${call.agentId ? 'text-gray-900' : 'italic text-gray-400'}`}>
                    {call.agentName || 'Unknown'}
                  </p>
                  {call.agentId && call.metadata?.originalAgentName ? (
                    <p className="text-xs italic text-gray-400">
                      {`${call.metadata.originalAgentName}`}
                    </p>
                  ) : null}
                  <p className="text-sm text-gray-400 mt-0.5">{formatDateTimeInTimezone(call.date, activeProfile?.timezone)}</p>
                </div>}

                {/* 3. Score */}
                <div className="flex flex-col items-center justify-between w-[125px] self-stretch">
                  {call.retellCallReport ? (
                    !call.callStarted ? (
                      call.errors && call.errors.length > 0 ? (
                        <div className="flex flex-col items-center">
                          <span className="text-red-600 font-bold text-xs uppercase text-center leading-tight">CALL<br />FAILED</span>
                          <button
                            onClick={() => setViewErrors(call.errors!)}
                            className="text-xs text-blue-600 hover:underline mt-1"
                          >
                            View Errors
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-900 font-bold text-xs uppercase text-center leading-tight">NOT<br />STARTED</span>
                      )
                    ) : !call.callEnded ? (
                      <>
                        <div className="flex flex-col items-center">
                          <span className="text-gray-900 font-bold text-xs uppercase text-center leading-tight">INCOMPLETE</span>
                          {call.incompleteReason && (
                            <span className="text-red-600 text-xs text-center mt-0.5">{call.incompleteReason}</span>
                          )}
                        </div>
                        <button
                          onClick={async () => { await rescoreCall(call.id); refreshRow(call.id); }}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 transition-colors"
                        >
                          Refresh
                        </button>
                      </>
                    ) : call.voicemail ? (
                      <span className="text-gray-900 font-bold text-xs uppercase text-center leading-tight">VOICEMAIL</span>
                    ) : (
                      <>
                        <div className="mt-1">
                          <ScoreBadge score={call.overallScore}/>
                        </div>
                        {!call.doNotCount && (
                          <button
                            onClick={async () => { await rescoreCall(call.id); refreshRow(call.id); }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 transition-colors"
                          >
                            Rescore
                          </button>
                        )}
                      </>
                    )
                  ) : null}
                </div>

                {/* 4. Call Details */}
                <div className="w-[225px]">
                  <p className="text-xs text-gray-400">Call Details</p>
                  <div className="text-sm mt-0.5 space-y-0.5">
                    {call.retellAgentId && (
                      <p>
                        AI Agent: <button
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          onClick={() => handleViewAIAgent(call.retellAgentId!)}
                        >
                          {call.retellAgentName || call.retellAgentId}
                        </button>
                      </p>
                    )}
                    {call.retellCallReport ? (
                      <p>
                        Report: <button
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          onClick={() => setViewCallReport(call.retellCallReport!)}
                        >
                          {call.retellCallReport.name}
                        </button>
                      </p>
                    ) : (
                      <button
                        onClick={() => setAssociateCallReportCall({ id: call.id, retellAgentId: call.retellAgentId })}
                        className="text-xs px-2 py-1 rounded font-medium bg-[#800000] text-white hover:bg-[#660000] transition-colors"
                      >
                        Associate Call Report
                      </button>
                    )}
                    {call.voiceId && (
                      <p>
                        Voice: <button
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          onClick={() => setViewVoiceId(call.voiceId!)}
                        >
                          {call.voiceName}
                        </button>
                      </p>
                    )}
                    {call.fromNumber && (
                      <p>
                        From: <button
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          onClick={() => setViewPhoneNumber(call.fromNumber!)}
                        >
                          {call.fromNumber}
                        </button>
                      </p>
                    )}
                    {call.llmVars && Object.keys(call.llmVars).length > 0 && (
                      <p>
                        <button
                          className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          onClick={() => setViewInputVars(call.llmVars!)}
                        >
                          Input Variables
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* 5. Moderation */}
              {(() => {
                const callDisabled = !call.callStarted || !call.callEnded || !!call.doNotCount;
                return (
                  <div className="w-[125px]">
                    <p className="text-xs text-gray-400">Moderation</p>
                    <div className="flex flex-col gap-1.5 mt-0.5">
                      <button
                        disabled={callDisabled || !call.propertyId || !call.retellCallReport}
                        onClick={async () => { await publishCall(call.id, !call.published); refreshRow(call.id); }}
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                          callDisabled || !call.propertyId || !call.retellCallReport
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : call.published
                              ? 'border border-green-600 text-green-600 bg-white hover:bg-green-50'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title={call.published ? 'Unpublish' : 'Publishto Client'}
                      >
                        {call.published ? 'Published' : 'Publish to Client'}
                      </button>
                      <button
                        disabled={callDisabled || !call.propertyId || !call.retellCallReport}
                        onClick={() => setEmailCallId(call.id)}
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                          callDisabled || !call.propertyId || !call.retellCallReport
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : call.emailed
                              ? 'bg-orange-500 text-white hover:bg-orange-600'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title="Email Call Report"
                      >
                        Email Call Report
                      </button>
                      {/* DoNotCount toggle */}
                      <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                        <span className="relative w-8 h-4 flex-shrink-0 inline-block">
                          <input
                            type="checkbox"
                            checked={!!call.doNotCount}
                            onChange={async () => {
                              const next = !call.doNotCount;
                              setData((prev) => prev.map((c) => c.id === call.id ? { ...c, doNotCount: next } : c));
                              try { await setDoNotCount(call.id, next); }
                              catch { await refreshRow(call.id); }
                            }}
                            className="opacity-0 w-0 h-0 absolute"
                          />
                          <span className={`absolute inset-0 rounded-full transition-colors ${call.doNotCount ? 'bg-red-500' : 'bg-gray-300'}`} />
                          <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${call.doNotCount ? 'translate-x-4' : ''}`} />
                        </span>
                        <span className="text-xs text-gray-600">Does Not Count</span>
                      </label>
                      {/* DoesCountReason button */}
                      {!call.doNotCount && (call.errors?.length || call.voicemail || call.incompleteReason) && (
                        <button
                          onClick={() => { setReasonInput(call.doesCountReason || ''); setReasonCallId(call.id); }}
                          className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                            call.doesCountReason
                              ? 'border border-red-400 text-red-600 bg-white hover:bg-red-50'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          Does Count Reason
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* 6. Call Audit / Call Report */}
              <div  className="w-[100px]">
                <p className="text-xs text-gray-400">View Call</p>
                <div className="flex flex-col gap-1.5 mt-0.5">
                  <button
                    onClick={() => setViewDataCallId(call.id)}
                    className="text-xs px-2 py-1 rounded text-white transition-colors w-full" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
                  >
                    Call Audit
                  </button>
                  <button
                    disabled={!call.callStarted || !call.callEnded}
                    onClick={() => setSelectedCallId(call.id)}
                    className={`text-xs px-2 py-1 rounded font-medium transition-colors w-full ${
                      !call.callStarted || !call.callEnded
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'text-white'
                    }`}
                    style={call.callStarted && call.callEnded ? { backgroundColor: 'var(--ion-color-secondary)' } : undefined}
                  >
                    Call Report
                  </button>
                </div>
              {/* 7. Cancel scheduled call */}
              {callTypeSegment === 'scheduled' && call.callStatus === 'scheduled' && (
                <div className="w-[100px]">
                  <div className="flex flex-col gap-1.5 mt-0.5">
                    <button
                      onClick={() => setCancelCallId(call.id)}
                      className="text-xs px-2 py-1 rounded font-medium transition-colors bg-red-100 text-red-700 hover:bg-red-200 w-full"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer pagination */}
      <div className="flex items-center justify-end gap-4 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={!hasPrev}
            className={`text-sm px-3 py-1 rounded ${hasPrev ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            &larr; Previous
          </button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasNext}
            className={`text-sm px-3 py-1 rounded ${hasNext ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Next &rarr;
          </button>
        </div>
      </div>

      {/* Date filter modal */}
      {showFilterModal && (
        <DateFilterModal
          startDate={startDate}
          endDate={endDate}
          onApply={(s, e) => { setStartDate(s); setEndDate(e); setPage(1); setShowFilterModal(false); }}
          onClose={() => setShowFilterModal(false)}
        />
      )}

      {/* Call detail drawer */}
      {selectedCallId && (
        <CallDetailDrawer
          callId={selectedCallId}
          onClose={() => setSelectedCallId(null)}
        />
      )}

      {/* Associate property modal */}
      {associatePropertyCallId && (
        <AssociatePropertyModal
          callId={associatePropertyCallId}
          onClose={() => setAssociatePropertyCallId(null)}
          onSuccess={async () => {
            await refreshRow(associatePropertyCallId);
            setAssociatePropertyCallId(null);
          }}
        />
      )}

      {/* Associate agent modal */}
      {associateAgentCall && (
        <AssociateAgentModal
          callId={associateAgentCall.id}
          callPropertyId={associateAgentCall.propertyId}
          onClose={() => setAssociateAgentCall(null)}
          onSuccess={async () => {
            await refreshRow(associateAgentCall.id);
            setAssociateAgentCall(null);
          }}
        />
      )}

      {/* Associate call report modal */}
      {associateCallReportCall && (
        <AssociateCallReportModal
          callId={associateCallReportCall.id}
          retellAgentId={associateCallReportCall.retellAgentId}
          onClose={() => setAssociateCallReportCall(null)}
          onSuccess={async () => {
            await refreshRow(associateCallReportCall.id);
            setAssociateCallReportCall(null);
          }}
        />
      )}

      {/* Create call modal */}
      {showCreateCallModal && (
        <CreateCallModal
          clientId={parentFilter?.clientId}
          propertyId={parentFilter?.propertyId}
          onClose={() => setShowCreateCallModal(false)}
          onSuccess={(callId) => { setShowCreateCallModal(false); fetch(); startPolling(callId); }}
          onError={() => fetch()}
        />
      )}

      {/* View call data modal */}
      {viewDataCallId && (
        <CallDataModal
          callId={viewDataCallId}
          onClose={() => setViewDataCallId(null)}
        />
      )}

      {/* Email call modal */}
      {emailCallId && (
        <EmailCallModal
          callId={emailCallId}
          onClose={() => setEmailCallId(null)}
          onSuccess={async () => {
            await refreshRow(emailCallId);
            setEmailCallId(null);
          }}
        />
      )}

      {/* View AI Agent modal */}
      {viewAIAgent && (
        <ViewAIAgentModal agent={viewAIAgent} onClose={() => setViewAIAgent(null)} />
      )}

      {/* View Call Report modal */}
      {viewCallReport && (
        <ViewCallReportModal report={viewCallReport} onClose={() => setViewCallReport(null)} />
      )}

      {/* View Voice modal */}
      {viewVoiceId && (
        <ViewVoiceModal voiceId={viewVoiceId} onClose={() => setViewVoiceId(null)} />
      )}

      {/* View Phone Calls modal */}
      {viewPhoneNumber && (
        <ViewPhoneCallsModal fromNumber={viewPhoneNumber} onClose={() => setViewPhoneNumber(null)} />
      )}

      {/* View Input Variables modal */}
      {viewInputVars && (
        <ViewInputVarsModal vars={viewInputVars} onClose={() => setViewInputVars(null)} />
      )}

      {/* View Errors modal */}
      {viewErrors && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewErrors(null)} />
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">Call Errors</h2>
            <ul className="space-y-2">
              {viewErrors.map((err, i) => (
                <li key={i} className="text-sm text-red-600">{err}</li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setViewErrors(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Does Count Reason modal */}
      {reasonCallId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReasonCallId(null)} />
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Does Count Reason</h2>
              <textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                rows={3}
                placeholder="Enter reason this call counts toward the budget..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                style={{ fieldSizing: 'content' } as React.CSSProperties}
              />
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={async () => {
                  await setDoesCountReason(reasonCallId, null);
                  refreshRow(reasonCallId);
                  setReasonCallId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
              >
                Clear
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReasonCallId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await setDoesCountReason(reasonCallId, reasonInput.trim() || null);
                    refreshRow(reasonCallId);
                    setReasonCallId(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit scheduled call modal */}
      {editScheduledCallId && (() => {
        const EditModal = () => {
          const [editCall, setEditCall] = useState<MysteryShopCall | null>(null);
          const [editDate, setEditDate] = useState('');
          const [editTime, setEditTime] = useState('');
          const [editTz, setEditTz] = useState('');
          const [editFromNumber, setEditFromNumber] = useState('');
          const [editVoice, setEditVoice] = useState('');
          const [editAgentName, setEditAgentName] = useState('');
          const [editInputVars, setEditInputVars] = useState<Record<string, string>>({});
          const [voices, setVoices] = useState<CallVoice[]>([]);
          const [phoneNumbers, setPhoneNumbers] = useState<CallPhoneNumber[]>([]);
          const [fnCollisions, setFnCollisions] = useState<CollisionResult[]>([]);
          const [voiceColls, setVoiceColls] = useState<CollisionResult[]>([]);
          const [editSaving, setEditSaving] = useState(false);
          const collTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

          useEffect(() => {
            getCallById(editScheduledCallId).then(c => {
              setEditCall(c);
              if (c.callDate) {
                // DB stores callDate in the call's timezone (not UTC-converted)
                // Parse as UTC to get the raw hours/minutes as stored
                const d = new Date(c.callDate);
                const pad = (n: number) => String(n).padStart(2, '0');
                setEditDate(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
                setEditTime(`${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`);
              }
              setEditTz(c.callDateTimezone || '');
              setEditFromNumber(c.fromNumber || '');
              setEditVoice(c.voiceId || '');

              // For scheduled calls, dynamic vars are in metadata.dynamicVars (not llmVars)
              const dynVars: Record<string, string> = {};
              const metaDynVars = (c.metadata as Record<string, unknown>)?.dynamicVars;
              if (metaDynVars && typeof metaDynVars === 'object') {
                for (const [k, v] of Object.entries(metaDynVars as Record<string, unknown>)) {
                  if (typeof v === 'string') dynVars[k] = v;
                }
              }
              // Fall back to llmVars if dynamicVars not available (placed calls)
              const vars = Object.keys(dynVars).length > 0 ? dynVars : (c.llmVars || {});
              setEditInputVars(vars);

              // Agent name: prefer struct field, fall back to dynamicVars
              setEditAgentName(c.agentName || dynVars['agent_name'] || dynVars['contact_ai_agent_name'] || '');
            });
            searchCallVoices({ status: 'active' }, { pageSize: 1000 }).then(setVoices);
            searchCallPhoneNumbers({ status: 'active' }, { pageSize: 1000 }).then(setPhoneNumbers);
          }, []);

          // Collision check
          useEffect(() => {
            setFnCollisions([]);
            setVoiceColls([]);
            if (!editCall?.clientId || !editCall?.propertyId) return;
            if (!editFromNumber && !editVoice) return;
            if (collTimer.current) clearTimeout(collTimer.current);
            collTimer.current = setTimeout(() => {
              checkCollision({ clientId: editCall.clientId!, propertyId: editCall.propertyId, fromNumber: editFromNumber, agentName: editAgentName, voiceId: editVoice }).then(resp => {
                setFnCollisions(resp.fromNumberCollisions || []);
                setVoiceColls(resp.voiceCollisions || []);
              }).catch(() => {});
            }, 500);
            return () => { if (collTimer.current) clearTimeout(collTimer.current); };
          }, [editFromNumber, editVoice, editAgentName, editCall?.clientId, editCall?.propertyId]);

          const handleSave = async () => {
            setEditSaving(true);
            try {
              const dataEntries = Object.entries(editInputVars).map(([k, v]) => ({ [k]: v }));
              await patchScheduledCall(editScheduledCallId, {
                callDate: `${editDate}T${editTime}:00`,
                callDateTimezone: editTz || undefined,
                voiceId: editVoice || undefined,
                agentName: editAgentName || undefined,
                data: dataEntries.length > 0 ? dataEntries : undefined,
              });
              setEditScheduledCallId(null);
              fetch();
            } catch {} finally { setEditSaving(false); }
          };

          const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
          const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

          const collisionWarning = (colls: CollisionResult[]) => {
            if (!colls.length) return null;
            const latest = colls[0];
            const d = new Date(latest.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
            return <p className="text-red-600 text-xs font-bold mt-1">Used at {latest.propertyName || 'unknown'} on {d}{colls.length > 1 ? ` (+${colls.length - 1} more)` : ''}</p>;
          };

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setEditScheduledCallId(null)} />
              <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
                <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Edit Scheduled Call</h2>
                  {!editCall ? <p className="text-sm text-gray-400">Loading...</p> : (
                    <div className="space-y-4">
                      <div>
                        <label className={labelClass}>Call Date & Time *</label>
                        <div className="flex items-center gap-2">
                          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className={`${inputClass} flex-1`} />
                          <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className={`${inputClass} w-28`} />
                          <select value={editTz} onChange={e => setEditTz(e.target.value)} className={`${inputClass} w-40`}>
                            <option value="">TZ</option>
                            <option value="Pacific/Honolulu">Hawaii</option>
                            <option value="America/Anchorage">Alaska</option>
                            <option value="America/Los_Angeles">Pacific</option>
                            <option value="America/Denver">Mountain</option>
                            <option value="America/Chicago">Central</option>
                            <option value="America/New_York">Eastern</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>From Number</label>
                        <select value={editFromNumber} onChange={e => setEditFromNumber(e.target.value)} className={inputClass}>
                          <option value="">Select number</option>
                          {phoneNumbers.map(n => <option key={n.id} value={n.phoneNumber}>{n.phoneNumber}</option>)}
                        </select>
                        {collisionWarning(fnCollisions)}
                      </div>
                      <div>
                        <label className={labelClass}>Voice</label>
                        <select value={editVoice} onChange={e => setEditVoice(e.target.value)} className={inputClass}>
                          <option value="">Select voice</option>
                          {voices.map(v => <option key={v.id} value={v.voiceId}>{v.name || v.voiceName}</option>)}
                        </select>
                        {collisionWarning(voiceColls)}
                      </div>
                      <div>
                        <label className={labelClass}>Agent Name</label>
                        <input type="text" value={editAgentName} onChange={e => setEditAgentName(e.target.value)} className={inputClass} placeholder="Agent name" />
                      </div>
                      {Object.keys(editInputVars).filter(k => k !== 'property_name' && k !== 'property_address' && k !== 'timezone' && k !== 'prio_a' && k !== 'end_a' && k !== 'agent_name' && k !== 'contact_ai_agent_name').map(key => (
                        <div key={key}>
                          <label className={labelClass}>{key}</label>
                          <input type="text" value={editInputVars[key] || ''} onChange={e => setEditInputVars(prev => ({ ...prev, [key]: e.target.value }))} className={inputClass} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button type="button" onClick={() => setEditScheduledCallId(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="button" onClick={handleSave} disabled={editSaving || !editDate || !editTime} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {editSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          );
        };
        return <EditModal />;
      })()}

      {/* Cancel scheduled call confirmation */}
      {cancelCallId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCancelCallId(null)} />
          <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
            <div className="px-6 pt-6 pb-4">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Cancel Scheduled Call</h2>
              <p className="text-sm text-gray-600">Are you sure you want to cancel this scheduled call?</p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setCancelCallId(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">No</button>
              <button
                type="button"
                disabled={cancelLoading}
                onClick={async () => {
                  setCancelLoading(true);
                  try {
                    await cancelScheduledCall(cancelCallId);
                    setCancelCallId(null);
                    fetch();
                  } catch {} finally { setCancelLoading(false); }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
